import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Typography,
  Paper,
  Stack,
  LinearProgress,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Chip,
  Avatar,
} from '@mui/material';
import InsightsIcon from '@mui/icons-material/Insights';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import { IconStat, RankBadge } from '../components/RowStats.js';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import AdsClickIcon from '@mui/icons-material/AdsClick';
import TrackChangesIcon from '@mui/icons-material/TrackChanges';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip as RTooltip,
  CartesianGrid,
  Cell,
} from 'recharts';
import { useMe } from '../api/hooks.js';
import { api } from '../api/client.js';
import { Header } from './SupportAnalyticsPage.js';
import { SessionDrilldownDialog, type DrilldownParams } from '../components/SessionDrilldownDialog.js';
import { useT } from '../i18n/useT.js';
import { useSort } from '../lib/useSort.js';

interface FrustratedUser { user: string; email: string | null; frustration: number; sessions: number; rageClicks: number }
interface Breakdown { label: string; count: number }
interface FrictionPage { url: string; sessions: number; rageClicks: number; deadClicks: number; errors: number }
interface Dropoff { from: string; to: string; rate: number; delta: number; sessions: number }
interface Behavior {
  totalSessions: number;
  avgDurationMs: number;
  bounceRate: number;
  avgFrustration: number;
  rageClicks: number;
  deadClicks: number;
  trends: { avgFrustration: number; rageClicks: number; deadClicks: number; avgDuration: number };
  frustrationTrend: { day: string; avg: number }[];
  hourly: { hour: number; count: number }[];
  frustrationBuckets: { label: string; count: number }[];
  devices: Breakdown[];
  browsers: Breakdown[];
  countries: Breakdown[];
  trafficSources: Breakdown[];
  entryPages: Breakdown[];
  frictionPages: FrictionPage[];
  dropoffs: Dropoff[];
}

/** High-level acquisition channel for each traffic-source label. */
const CHANNEL_OF: Record<string, string> = {
  ChatGPT: 'AI', Perplexity: 'AI', Gemini: 'AI', Claude: 'AI', Copilot: 'AI',
  Google: 'Search', Bing: 'Search', DuckDuckGo: 'Search', Yahoo: 'Search', Yandex: 'Search', Baidu: 'Search', Ecosia: 'Search',
  Facebook: 'Social', Instagram: 'Social', 'X (Twitter)': 'Social', LinkedIn: 'Social', YouTube: 'Social',
  TikTok: 'Social', Reddit: 'Social', Pinterest: 'Social', WhatsApp: 'Social', Telegram: 'Social',
  Direct: 'Direct',
};
/** Rolls a per-source breakdown up into per-channel totals. */
function toChannels(sources: Breakdown[]): Breakdown[] {
  const m = new Map<string, number>();
  for (const s of sources) {
    const ch = CHANNEL_OF[s.label] ?? 'Referral';
    m.set(ch, (m.get(ch) ?? 0) + s.count);
  }
  return [...m].map(([label, count]) => ({ label, count })).sort((a, b) => b.count - a.count);
}

const BUCKET_COLOR: Record<string, string> = {
  '0': '#2ec2b3', '1-19': '#84cc16', '20-39': '#eab308', '40-69': '#f59e0b', '70-100': '#ef4444',
};
const EMERALD = '#2ec2b3';

export function UserBehaviorPage() {
  const all = useT();
  const t = all.insights.behavior;
  const drills = all.insights.support.drill;
  const { data: me } = useMe();
  const orgId = me?.organizations[0]?.id;
  const [days, setDays] = useState(7);

  const q = useQuery({
    enabled: !!orgId,
    queryKey: ['user-behavior', orgId, days],
    queryFn: async () => (await api.get(`/v1/orgs/${orgId}/insights/behavior?days=${days}`)).data as Behavior,
  });
  const d = q.data;

  const usersQ = useQuery({
    enabled: !!orgId,
    queryKey: ['frustrated-users', orgId, days],
    queryFn: async () =>
      (await api.get(`/v1/orgs/${orgId}/insights/frustrated-users?days=${days}`)).data.data as FrustratedUser[],
  });
  const sort = useSort<'user' | 'account' | 'frustration' | 'sessions' | 'rageClicks'>('frustration');
  const userRows = sort.sorted(usersQ.data ?? [], {
    user: (u) => u.email ?? u.user,
    account: (u) => u.email?.split('@')[1],
    frustration: (u) => u.frustration,
    sessions: (u) => u.sessions,
    rageClicks: (u) => u.rageClicks,
  });

  // Drill-down popup: which sessions sit behind a clicked report row/metric.
  const [drill, setDrill] = useState<{ title: string; subtitle?: string; params: DrilldownParams } | null>(null);
  const openDrill = (title: string, extra: Partial<DrilldownParams>, subtitle?: string) =>
    setDrill({ title, subtitle, params: { days, ...extra } });

  return (
    <Box>
      <Header
        icon={<InsightsIcon color="primary" />}
        title={t.title}
        subtitle={t.subtitle}
        days={days}
        setDays={setDays}
      />
      {q.isLoading && <LinearProgress sx={{ mb: 2 }} />}

      {/* Trend KPI row */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 2, mb: 2 }}>
        <TrendKpi icon={<WarningAmberIcon />} tint="#f59e0b" value={`${d?.avgFrustration ?? 0}/100`} label={t.kpi.frustration} trend={d?.trends.avgFrustration} onClick={() => openDrill(drills.frustrated, { minFrustration: 40 }, drills.frustratedHint)} />
        <TrendKpi icon={<AdsClickIcon />} tint="#ef4444" value={d?.rageClicks ?? 0} label={t.kpi.rage} trend={d?.trends.rageClicks} onClick={() => openDrill(drills.rage, { hasRageClick: true })} />
        <TrendKpi icon={<TrackChangesIcon />} tint="#3b82f6" value={d?.deadClicks ?? 0} label={t.kpi.dead} trend={d?.trends.deadClicks} onClick={() => openDrill(drills.dead, { hasDeadClick: true })} />
        <TrendKpi icon={<AccessTimeIcon />} tint={EMERALD} value={d ? fmtDur(d.avgDurationMs) : '—'} label={t.kpi.avgSession} trend={d?.trends.avgDuration} />
      </Box>

      {/* Trend + activity charts */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '3fr 2fr' }, gap: 2, mb: 2 }}>
        <ChartCard title={t.trendTitle} subtitle={t.trendSubtitle}>
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={d?.frustrationTrend ?? []}>
              <defs>
                <linearGradient id="frGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#f59e0b" stopOpacity={0.5} />
                  <stop offset="100%" stopColor="#f59e0b" stopOpacity={0.04} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" opacity={0.15} />
              <XAxis dataKey="day" fontSize={11} tickFormatter={fmtDay} />
              <YAxis domain={[0, 100]} fontSize={11} />
              <RTooltip labelFormatter={fmtDay} />
              <Area type="monotone" dataKey="avg" stroke="#f59e0b" strokeWidth={2} fill="url(#frGrad)" name={t.avgFrustration} />
            </AreaChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title={t.activityTitle} subtitle={t.activitySubtitle}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={d?.hourly ?? []}>
              <CartesianGrid strokeDasharray="3 3" opacity={0.12} vertical={false} />
              <XAxis dataKey="hour" fontSize={10} interval={1} tickFormatter={(h) => String(h).padStart(2, '0')} />
              <YAxis allowDecimals={false} fontSize={11} />
              <RTooltip labelFormatter={(h) => `${String(h).padStart(2, '0')}:00 UTC`} />
              <Bar
                dataKey="count"
                fill={EMERALD}
                radius={[3, 3, 0, 0]}
                name={t.sessions}
                cursor="pointer"
                onClick={(p: { hour?: number }) =>
                  p?.hour != null && openDrill(`${String(p.hour).padStart(2, '0')}:00 UTC. Sessions`, { hour: p.hour }, 'Sessions started in this hour')
                }
              />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </Box>

      {/* Frustration distribution */}
      <Paper sx={{ p: 2.5, mb: 2 }}>
        <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>{t.distribution}</Typography>
        <Box sx={{ height: 200 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={d?.frustrationBuckets ?? []}>
              <XAxis dataKey="label" fontSize={12} />
              <YAxis allowDecimals={false} fontSize={11} />
              <RTooltip />
              <Bar
                dataKey="count"
                radius={[4, 4, 0, 0]}
                cursor="pointer"
                onClick={(p: { label?: string }) => {
                  if (!p?.label) return;
                  const [min, max] = bucketRange(p.label);
                  openDrill(t.band(p.label), { minFrustration: min, maxFrustration: max }, t.bandHint);
                }}
              >
                {(d?.frustrationBuckets ?? []).map((b) => (<Cell key={b.label} fill={BUCKET_COLOR[b.label] ?? '#64748b'} />))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Box>
      </Paper>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 2, mb: 2 }}>
        <BreakdownCard title={t.trafficSources} rows={d?.trafficSources} onRowClick={(r) => openDrill(t.sourceSessions(r.label), { trafficSource: r.label }, t.sourceHint)} />
        <BreakdownCard title={t.byChannel} rows={d ? toChannels(d.trafficSources) : undefined} />
        <BreakdownCard title={t.countries} rows={d?.countries} />
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 2, mb: 2 }}>
        <BreakdownCard title={t.devices} rows={d?.devices} onRowClick={(r) => openDrill(t.sourceSessions(r.label), { device: r.label }, t.deviceHint)} />
        <BreakdownCard title={t.browsers} rows={d?.browsers} />
        <BreakdownCard title={t.entryPages} rows={d?.entryPages} truncate onRowClick={(r) => openDrill(prettyUrl(r.label), { entryPath: r.label }, t.entryHint)} />
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
        {/* Click Anomalies by Page */}
        <Paper sx={{ p: 2.5 }}>
          <Typography variant="subtitle1" fontWeight={700}>{t.anomaliesByPage}</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
            Pages with the most rage clicks, dead clicks &amp; errors
          </Typography>
          <Stack spacing={1.75}>
            {(() => {
              const rows = d?.frictionPages ?? [];
              const max = Math.max(1, ...rows.map((p) => p.rageClicks + p.deadClicks + p.errors));
              return rows.slice(0, 6).map((p, i) => (
                <Stack
                  key={p.url}
                  direction="row"
                  alignItems="center"
                  spacing={1.5}
                  onClick={() => openDrill(prettyUrl(p.url), { frictionPath: p.url }, t.lastPageHint)}
                  sx={{ cursor: 'pointer', borderRadius: 1, mx: -0.5, px: 0.5, py: 0.25, '&:hover': { bgcolor: 'action.hover' } }}
                >
                  <Typography variant="caption" color="text.disabled" sx={{ width: 14 }}>{i + 1}</Typography>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" noWrap sx={{ mb: 0.5 }}>{prettyUrl(p.url)}</Typography>
                    <Box sx={{ display: 'flex', height: 6, borderRadius: 3, overflow: 'hidden', bgcolor: 'action.hover' }}>
                      <Box sx={{ width: `${(p.rageClicks / max) * 100}%`, bgcolor: '#ef4444' }} />
                      <Box sx={{ width: `${(p.deadClicks / max) * 100}%`, bgcolor: '#3b82f6' }} />
                      <Box sx={{ width: `${(p.errors / max) * 100}%`, bgcolor: '#f59e0b' }} />
                    </Box>
                  </Box>
                  <Box sx={{ textAlign: 'right', width: 34 }}>
                    <Typography variant="body2" fontWeight={700} sx={{ color: '#ef4444' }}>{p.rageClicks}</Typography>
                    <Typography variant="caption" color="text.disabled">rage</Typography>
                  </Box>
                  <Box sx={{ textAlign: 'right', width: 34 }}>
                    <Typography variant="body2" fontWeight={700} sx={{ color: '#3b82f6' }}>{p.deadClicks}</Typography>
                    <Typography variant="caption" color="text.disabled">dead</Typography>
                  </Box>
                  <Box sx={{ textAlign: 'right', width: 34 }}>
                    <Typography variant="body2" fontWeight={700} sx={{ color: '#f59e0b' }}>{p.errors}</Typography>
                    <Typography variant="caption" color="text.disabled">err</Typography>
                  </Box>
                </Stack>
              ));
            })()}
            {d && d.frictionPages.length === 0 && <Typography color="text.disabled" variant="body2">{t.noAnomalies}</Typography>}
          </Stack>
        </Paper>

        {/* Top Drop-off Points */}
        <Paper sx={{ p: 2.5 }}>
          <Typography variant="subtitle1" fontWeight={700}>{t.dropoffs}</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
            Where users abandon their journey
          </Typography>
          <Stack spacing={1.25}>
            {(d?.dropoffs ?? []).map((x) => (
              <Box key={`${x.from}->${x.to}`} sx={{ p: 1.5, borderRadius: 2, bgcolor: 'action.hover' }}>
                <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" fontWeight={600} noWrap>{prettyUrl(x.from)} → {x.to === '(exit)' ? 'exit' : prettyUrl(x.to)}</Typography>
                    <Box sx={{ mt: 0.75, height: 6, borderRadius: 3, bgcolor: 'background.paper', overflow: 'hidden', maxWidth: 200 }}>
                      <Box sx={{ width: `${x.rate}%`, height: '100%', bgcolor: '#ef4444' }} />
                    </Box>
                  </Box>
                  <Box sx={{ textAlign: 'right' }}>
                    <Typography variant="h6" fontWeight={700}>{x.rate}%</Typography>
                    {x.delta !== 0 && (
                      <Typography variant="caption" sx={{ color: x.delta > 0 ? '#ef4444' : '#2ec2b3' }}>
                        {x.delta > 0 ? '+' : ''}{x.delta}%
                      </Typography>
                    )}
                  </Box>
                </Stack>
              </Box>
            ))}
            {d && d.dropoffs.length === 0 && <Typography color="text.disabled" variant="body2">{t.notEnoughJourney}</Typography>}
          </Stack>
        </Paper>
      </Box>

      {/* Most Frustrated Users. Identified visitors ranked by peak frustration. */}
      <Paper sx={{ p: 2.5, mt: 2 }}>
        <Typography variant="subtitle1" fontWeight={700}>{t.frustratedUsers}</Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
          Users with the highest frustration scores this period
        </Typography>
        {usersQ.data && usersQ.data.length === 0 ? (
          <Typography color="text.disabled" variant="body2" sx={{ py: 2 }}>
            No identified users with frustration signals yet.
          </Typography>
        ) : (
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small" sx={{ minWidth: 640 }}>
              <TableHead>
                <TableRow>
                  {sort.head('user', t.columns.user)}
                  {sort.head('account', t.columns.account)}
                  {sort.head('frustration', t.columns.frustration, 'center')}
                  {sort.head('sessions', t.columns.sessions, 'right')}
                  {sort.head('rageClicks', t.columns.rageClicks, 'right')}
                </TableRow>
              </TableHead>
              <TableBody>
                {userRows.map((u, i) => (
                  <TableRow
                    key={u.user}
                    hover
                    onClick={() =>
                      openDrill(
                        nameOf(u.email ?? u.user),
                        u.email ? { userEmail: u.email } : { userId: u.user },
                        `Sessions for this user · frustration ${u.frustration}/100`,
                      )
                    }
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell>
                      <Stack direction="row" alignItems="center" spacing={1.25}>
                        <RankBadge rank={i + 1} />
                        <Avatar sx={{ width: 30, height: 30, fontSize: 12, fontWeight: 700, bgcolor: 'primary.main' }}>
                          {initialsOf(u.email ?? u.user)}
                        </Avatar>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="body2" fontWeight={600} noWrap>{nameOf(u.email ?? u.user)}</Typography>
                          {u.email && (
                            <Typography variant="caption" color="text.secondary" noWrap>{u.email}</Typography>
                          )}
                        </Box>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">{accountOf(u.email)}</Typography>
                    </TableCell>
                    <TableCell align="center">
                      <FrustrationBadge score={u.frustration} />
                    </TableCell>
                    <TableCell align="right"><Typography variant="body2" fontWeight={600}>{u.sessions}</Typography></TableCell>
                    <TableCell align="right">
                      <IconStat icon={<LocalFireDepartmentIcon />} value={u.rageClicks} color="#f59e0b" title={t.columns.rageClicks} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}
      </Paper>

      <SessionDrilldownDialog
        open={!!drill}
        onClose={() => setDrill(null)}
        orgId={orgId}
        title={drill?.title ?? ''}
        subtitle={drill?.subtitle}
        params={drill?.params ?? { days }}
      />
    </Box>
  );
}

/** Parses a frustration bucket label ("0", "40-69", "70-100") into [min, max]. */
function bucketRange(label: string): [number, number] {
  if (label === '0') return [0, 0];
  const [lo, hi] = label.split('-');
  return [Number(lo) || 0, Number(hi ?? lo) || 100];
}

/** "sarah@megacorp.com" → "megacorp.com"; blank for unidentified users. */
function accountOf(email: string | null): string {
  return email?.split('@')[1] ?? '—';
}

/** A readable display label from an email local-part (or the raw identifier). */
function nameOf(id: string): string {
  const local = id.includes('@') ? id.split('@')[0]! : id;
  return local
    .split(/[._-]+/)
    .filter(Boolean)
    .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
    .join(' ') || id;
}

function initialsOf(id: string): string {
  const name = nameOf(id);
  const parts = name.split(' ');
  return ((parts[0]?.[0] ?? '') + (parts[1]?.[0] ?? '')).toUpperCase() || '?';
}

/** Score pill matching the report: red ≥80, amber ≥50, neutral below. */
function FrustrationBadge({ score }: { score: number }) {
  const tone = score >= 80 ? '#ef4444' : score >= 50 ? '#f59e0b' : '#64748b';
  return (
    <Chip
      size="small"
      label={`${score}/100`}
      sx={{ fontWeight: 700, color: tone, bgcolor: `${tone}1f`, minWidth: 68 }}
    />
  );
}

function TrendKpi({ icon, tint, value, label, trend, onClick }: { icon: React.ReactNode; tint: string; value: string | number; label: string; trend?: number; onClick?: () => void }) {
  // Lower is better for every metric here, so an increase reads as "worse".
  const up = (trend ?? 0) > 0;
  const flat = (trend ?? 0) === 0;
  const color = flat ? '#64748b' : up ? '#ef4444' : '#2ec2b3';
  return (
    <Paper onClick={onClick} sx={{ p: 2.5, cursor: onClick ? 'pointer' : 'default', '&:hover': onClick ? { boxShadow: 3 } : undefined }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
        <Box sx={{ width: 40, height: 40, borderRadius: 2, display: 'grid', placeItems: 'center', color: tint, bgcolor: `${tint}1f` }}>
          {icon}
        </Box>
        {trend != null && (
          <Stack direction="row" alignItems="center" spacing={0.25} sx={{ color }}>
            {flat ? null : up ? <TrendingUpIcon sx={{ fontSize: 16 }} /> : <TrendingDownIcon sx={{ fontSize: 16 }} />}
            <Typography variant="caption" fontWeight={700}>{up ? '+' : ''}{trend}%</Typography>
          </Stack>
        )}
      </Stack>
      <Typography variant="h4" fontWeight={700}>{value}</Typography>
      <Typography variant="body2" color="text.secondary">{label}</Typography>
    </Paper>
  );
}

function ChartCard({ title, subtitle, children }: { title: string; subtitle: string; children: React.ReactNode }) {
  return (
    <Paper sx={{ p: 2.5 }}>
      <Typography variant="subtitle1" fontWeight={700}>{title}</Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>{subtitle}</Typography>
      <Box sx={{ height: 240 }}>{children}</Box>
    </Paper>
  );
}

function BreakdownCard({ title, rows, truncate, onRowClick }: { title: string; rows?: Breakdown[]; truncate?: boolean; onRowClick?: (r: Breakdown) => void }) {
  const t = useT().insights.behavior;
  const total = (rows ?? []).reduce((n, r) => n + r.count, 0) || 1;
  return (
    <Paper sx={{ p: 2.5 }}>
      <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1.5 }}>{title}</Typography>
      <Stack spacing={1}>
        {(rows ?? []).length === 0 && <Typography color="text.disabled" variant="body2">{t.noData}</Typography>}
        {(rows ?? []).map((r) => (
          <Box
            key={r.label}
            onClick={onRowClick ? () => onRowClick(r) : undefined}
            sx={onRowClick ? { cursor: 'pointer', borderRadius: 1, mx: -0.5, px: 0.5, py: 0.25, '&:hover': { bgcolor: 'action.hover' } } : undefined}
          >
            <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.25 }}>
              <Typography variant="body2" noWrap sx={{ maxWidth: truncate ? 180 : undefined }}>
                {truncate ? prettyUrl(r.label) : r.label}
              </Typography>
              <Typography variant="body2" fontWeight={700}>{r.count}</Typography>
            </Stack>
            <Box sx={{ height: 6, borderRadius: 3, bgcolor: 'action.hover', overflow: 'hidden' }}>
              <Box sx={{ width: `${(r.count / total) * 100}%`, height: '100%', bgcolor: EMERALD }} />
            </Box>
          </Box>
        ))}
      </Stack>
    </Paper>
  );
}

function fmtDur(ms: number): string {
  const s = Math.round(ms / 1000);
  return s >= 60 ? `${Math.floor(s / 60)}m ${s % 60}s` : `${s}s`;
}
function fmtDay(iso: string): string {
  const d = new Date(iso + 'T00:00:00Z');
  const p = (n: number) => String(n).padStart(2, '0');
  return `${p(d.getUTCDate())}/${p(d.getUTCMonth() + 1)}`;
}
function prettyUrl(raw: string): string {
  try {
    const u = new URL(raw);
    return (u.pathname + u.hash) || '/';
  } catch {
    return raw;
  }
}
